/**
 * A MIDI keyboard playing into the editor.
 *
 * The device is the host's business and the music is the package's. Web MIDI lives here
 * because it is a browser API and `@app/score-editor` is meant to run without a browser;
 * what a key *means* -- which note it writes, in which part, spelled how -- is `pressed` and
 * the entry machine, and nothing about it is decided in this file.
 *
 * Nothing is asked for until the user asks. A browser shows a permission prompt for MIDI,
 * and a board that raises one on arrival is a board that has spent the user's attention
 * before it has written a note, so the prompt comes from the menu item and from nowhere
 * else. With no keyboard chosen, no port is listened to at all.
 *
 * A played key is heard as well as written. A controller usually makes no sound of its own,
 * and a player who cannot hear what they just played has no way of knowing they are an
 * octave out until they look.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { NOTHING_HELD, echo, pressed } from "@app/score-editor";
import type { AudioEngine, EntryInput, HeldKeys } from "@app/score-editor";

/** A keyboard the browser is willing to hand over, as a menu needs it. */
export interface MidiKeyboard {
  readonly id: string;
  readonly name: string;
}

export interface ScoreMidi {
  /** Whether this browser has Web MIDI at all. Safari long had none. */
  readonly possible: boolean;
  /** Whether the keyboards have been asked for yet. */
  readonly asked: boolean;
  readonly keyboards: readonly MidiKeyboard[];
  /** The keyboard being listened to, or `null` when none is. */
  readonly listening: string | null;
  /** Why there is no keyboard, in a sentence, or `null` when nothing went wrong. */
  readonly refusal: string | null;
  /** Ask the browser for the keyboards. Call this from a gesture: it may prompt. */
  ask(): void;
  /** Listen to one keyboard, or stop listening with `null`. */
  listen(id: string | null): void;
}

function named(input: MIDIInput): MidiKeyboard {
  return { id: input.id, name: input.name ?? "A MIDI keyboard" };
}

export function useScoreMidi(
  type: (input: EntryInput) => void,
  wake: () => AudioEngine,
): ScoreMidi {
  const possible = typeof navigator.requestMIDIAccess === "function";
  const [access, setAccess] = useState<MIDIAccess | null>(null);
  const [keyboards, setKeyboards] = useState<readonly MidiKeyboard[]>([]);
  const [listening, setListening] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  // The keys down and the entry machine are both reached from the port's own listener, which
  // is attached once per keyboard. Holding them in refs is what keeps a note arriving from
  // re-attaching the listener, which would drop whatever was held at that moment.
  const held = useRef<HeldKeys>(NOTHING_HELD);
  const typing = useRef(type);
  typing.current = type;
  const engine = useRef<AudioEngine | null>(null);

  useEffect(() => {
    if (access === null) return undefined;
    const list = (): void => setKeyboards([...access.inputs.values()].map(named));
    list();
    // A keyboard attached or unplugged while the board is open changes the menu under the
    // user's hand, which is the behaviour anyone plugging one in expects.
    access.addEventListener("statechange", list);
    return () => access.removeEventListener("statechange", list);
  }, [access]);

  // A keyboard that has been unplugged is not being listened to, whatever was chosen before.
  useEffect(() => {
    if (listening !== null && !keyboards.some((keyboard) => keyboard.id === listening)) {
      setListening(null);
    }
  }, [keyboards, listening]);

  useEffect(() => {
    const input = access === null || listening === null ? undefined : access.inputs.get(listening);
    if (input === undefined) return undefined;
    const hear = (event: MIDIMessageEvent): void => {
      if (event.data === null) return;
      const press = pressed(held.current, event.data);
      held.current = press.held;
      if (press.played === null) return;
      typing.current(press.played);
      if (engine.current !== null) echo(engine.current, press.played.note);
    };
    input.addEventListener("midimessage", hear);
    return () => {
      input.removeEventListener("midimessage", hear);
      held.current = NOTHING_HELD;
    };
  }, [access, listening]);

  const ask = useCallback((): void => {
    if (!possible) {
      setRefusal("this browser will not let a page listen to a MIDI keyboard");
      return;
    }
    // The gesture that asks for the keyboard is also the gesture the audio context needs, so
    // the echo is ready before the first key is played rather than silent for one note.
    engine.current = wake();
    if (access !== null) return;
    navigator.requestMIDIAccess().then(
      (granted) => {
        setRefusal(null);
        setAccess(granted);
        // One keyboard is no choice at all: asking for a keyboard when exactly one is
        // attached means that one.
        const only = [...granted.inputs.values()];
        if (only.length === 1 && only[0] !== undefined) setListening(only[0].id);
      },
      (reason: unknown) => {
        setRefusal(reason instanceof Error ? reason.message : "the keyboards were not handed over");
      },
    );
  }, [access, possible, wake]);

  const listen = useCallback(
    (id: string | null): void => {
      if (id !== null) engine.current = wake();
      setListening(id);
    },
    [wake],
  );

  return { possible, asked: access !== null, keyboards, listening, refusal, ask, listen };
}
